import { useState } from "react";
import { Link } from "wouter";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Loader2, ArrowLeft } from "lucide-react";
import bgDecorative from "@/assets/onboarding/bg-decorative.svg";
import logo from "@/assets/Logo.png";
import LanguageSwitcher from "@/components/layout/LanguageSwitcher";
export default function ForgotPassword() {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to send reset email");
      }

      const data = await response.json();
      
      setEmailSent(true);
      toast({
        title: "Reset Email Sent",
        description: data.message,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to send reset email",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col relative"
      style={{
        backgroundImage: `url(${bgDecorative})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="absolute top-4 right-4 z-10">
        <LanguageSwitcher />
      </div>

      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-[560px] bg-white rounded-3xl shadow-[0px_8px_10px_-6px_rgba(0,0,0,0.10)] shadow-xl outline outline-1 outline-offset-[-1px] outline-slate-200 overflow-hidden">
          <div className="p-16 flex flex-col items-center gap-8">
            <img className="w-24 h-9 object-contain" src={logo} alt="180Pi" />

            <div className="w-full flex flex-col items-center gap-2">
              <h1 className="text-center text-3xl font-extrabold leading-9" style={{ color: "#00444C" }}>
                {"Reset Password"}
              </h1>
              <p className="text-center text-slate-600 text-base font-normal leading-6">
                {emailSent ? "Check your email for a reset link" : "Enter your email address and we'll send you a reset link"}
              </p>
            </div>

            {emailSent ? (
              <div className="w-full flex flex-col gap-5">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-5 text-center">
                  <p className="text-sm font-medium leading-6 text-slate-700">
                    {"We've sent a password reset link to"} <strong>{email}</strong>
                  </p>
                  <p className="mt-2 text-xs leading-5 text-slate-500">
                    {"The link will expire in 24 hours. If you don't see the email, check your spam folder."}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => setEmailSent(false)}
                  className="w-full px-4 py-3 rounded-2xl bg-white outline outline-1 outline-offset-[-1px] outline-slate-200 text-sm font-bold leading-5 text-slate-700 transition-colors hover:bg-slate-50"
                >
                  {"Send to Different Email"}
                </button>

                <Link
                  href="/login"
                  className="w-full px-4 py-3 rounded-2xl flex items-center justify-center gap-2 text-sm font-medium leading-5 transition-colors hover:bg-slate-50"
                  style={{ color: "#00444C" }}
                >
                  <ArrowLeft className="h-4 w-4" />
                  {"Back to Login"}
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="w-full flex flex-col gap-5">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="email" className="text-slate-700 text-sm font-semibold leading-5">
                    {"Email Address"}
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder={"name@company.com"}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={isLoading}
                    className="px-4 py-3 bg-white rounded-2xl outline outline-1 outline-offset-[-1px] outline-slate-200 text-sm text-slate-700 placeholder:text-gray-500"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full px-4 py-3 rounded-2xl shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] flex justify-center items-center gap-2 text-white text-sm font-bold leading-5 disabled:opacity-50 cursor-pointer transition-opacity hover:opacity-90"
                  style={{ backgroundColor: "#00444C" }}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      {"Sending..."}
                    </>
                  ) : (
                    "Send Reset Link"
                  )}
                </button>

                <Link
                  href="/login"
                  className="w-full px-4 py-3 rounded-2xl flex items-center justify-center gap-2 text-sm font-medium leading-5 transition-colors hover:bg-slate-50"
                  style={{ color: "#00444C" }}
                >
                  <ArrowLeft className="h-4 w-4" />
                  {"Back to Login"}
                </Link>
              </form>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
